import { Form, FormApi } from 'informed';
import {
  AuthContext,
  capitalize,
  useApi,
  Payment,
  CommonContext,
  updateBonusSettings,
} from '@repo-level/core-front';
import React, {
  FC,
  forwardRef,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useCurrentRoute, useNavigation } from 'react-navi';

import { AnalyticsEventName } from 'api/analytics';
import { Button } from '../buttons';
import { PaymentMethod as PaymentMethodComp } from './PaymentMethod';
import { fetchPaymentMethods, submitFormPayment } from 'helpers/payments';
import { ModalDefaultProps } from 'components/Modal';
import { InputPromo } from './InputPromo';
import { DepositBonuses } from './DepositBonuses';
import { Spinner } from 'components/Spinner';
import { groupMemberBR } from 'helpers/user';
import { FullInformation } from 'components/auth/FullInformation';
import { PaymentMethodCard } from './PaymentMethod/components/PaymentMethodCard';
import { Registration } from 'components/auth/Registration';
import { checkCompleteUserInformation } from './helpers';
import {
  LocalStorageDeposit,
  removeQueryParameterWithNavigation,
} from 'helpers/common';
import { useAnalytics } from 'helpers/analytics';
import { Country } from 'helpers/conf';

import {
  ScCurrencyUser,
  ScDepositContent,
  ScDepositNav,
  ScDepositRow,
  ScDepositTitle,
  ScDepositWrapper,
  ScNotFound,
  ScPaymentMethodCompWrapper,
  ScPaymentsMethods,
  ScWrapperPromo,
} from './styled';
import { getCurrencyCentsAndCode } from 'helpers/wallet';
import { CRYPTO_METHOD } from './PaymentMethod/helpers';
import { ModalTemplate } from 'components/ModalTemplate';
import { ModalTemplateSettings } from 'components/ModalTemplate/types';
import { CommonUtilsContext } from 'contexts/commonUtils';
import { Select } from 'components/form/Select';
import { ModalsContext, ModalType } from 'contexts/modal';
import { PaymentMethodCardMobile } from './PaymentMethod/components/PaymentMethodCardMobile';
import { FormGroup } from 'components/form/groups/FormGroup';
import { Checkbox } from 'components/form/Checkbox';
import { REASON_FOR_UPDATING_USER } from '@repo-level/core-front/contexts/auth/reducer';

interface CurrencyApi {
  currency: string;
}

interface MethodApi {
  method: string;
}

export const Deposit = forwardRef<HTMLDivElement, ModalDefaultProps>(
  ({ onClose, onScrollTo }, ref) => {
    const { user, pendingUser } = useContext(AuthContext);
    const { currentIp, currencies } = useContext(CommonContext);
    const { isMobileS } = useContext(CommonUtilsContext);

    const navigation = useNavigation();
    const {
      url: { query },
    } = useCurrentRoute();

    const modalSettings: ModalTemplateSettings = useMemo(() => {
      return isMobileS
        ? {
            type: 'mobile',
            mode: { type: 'insideCrossButton' },
            smallYPadding: true,
          }
        : {
            type: 'desktop',
            size: 'xLarge',
            mode: { type: 'default' },
          };
    }, [isMobileS]);

    const removeVipQuery = useCallback(() => {
      if (query.vip) {
        removeQueryParameterWithNavigation(navigation, query, 'vip');
      }
    }, [navigation, query]);

    const handleClose = useCallback(() => {
      removeVipQuery();

      onClose && onClose();
    }, [onClose, removeVipQuery]);

    const handleCloseDeposit = useCallback(() => {
      removeVipQuery();

      LocalStorageDeposit.remove();

      onClose && onClose();
    }, [onClose, removeVipQuery]);

    if (user?.id == null && !pendingUser) {
      return <Registration onClose={handleClose} ref={ref} />;
    }

    if (
      (!checkCompleteUserInformation(
        user,
        currentIp?.countryCode === Country.France
      ) ||
        user?.state === null) &&
      currencies.find(({ code }) => code === user?.currency)?.fiat
    ) {
      return <FullInformation onClose={handleClose} ref={ref} />;
    }

    return (
      <ModalTemplate
        ref={ref}
        onClose={handleCloseDeposit}
        settings={modalSettings}
        positionCrossButton={isMobileS ? { top: 16, right: 16 } : undefined}
        isCrossSquare={isMobileS ? false : true}
      >
        <DepositComponent onScrollTo={onScrollTo} />
      </ModalTemplate>
    );
  }
);

const DepositComponent: FC<ModalDefaultProps> = ({ onScrollTo }) => {
  const { t } = useTranslation();

  const refDepositForm = useRef<HTMLDivElement>(null);

  const { user, fetchUser } = useContext(AuthContext);

  const { sendEventToAnalytics } = useAnalytics();

  const isBonusRestricted = useMemo(() => groupMemberBR(user), [user]);

  const isUserIssueBonuses = user?.canIssueBonuses;

  const [
    currentPaymentMethod,
    setCurrentPaymentMethod,
  ] = useState<Payment.Method>();

  const [
    requestPaymentMethods,
    { data: paymentMethods, pending: pendingPaymentMethods },
  ] = useApi(fetchPaymentMethods, undefined);

  const localPaymentMethod = LocalStorageDeposit.get().paymentMethod;

  const [requestUpdateBonusSettings] = useApi(updateBonusSettings);

  const isLocalMethodAvailable = useMemo(() => {
    return (
      paymentMethods?.some((method) => method.id === localPaymentMethod?.id) ??
      false
    );
  }, [localPaymentMethod?.id, paymentMethods]);

  useEffect(() => {
    if (user?.currency == null) return;
    requestPaymentMethods(user?.currency);
  }, [requestPaymentMethods, user]);

  useEffect(() => {
    if (pendingPaymentMethods || paymentMethods == null) return;

    if (!!localPaymentMethod && isLocalMethodAvailable) {
      setCurrentPaymentMethod(localPaymentMethod);
      return;
    }

    setCurrentPaymentMethod(paymentMethods[0]);

    return () => {
      setCurrentPaymentMethod(undefined);
    };
  }, [
    setCurrentPaymentMethod,
    paymentMethods,
    pendingPaymentMethods,
    isLocalMethodAvailable,
  ]);

  const handleSelectPaymentMethods = useCallback(
    (method: Payment.Method) => {
      setCurrentPaymentMethod(method);
      LocalStorageDeposit.push({ paymentMethod: method });

      onScrollTo && onScrollTo(refDepositForm);
    },
    [onScrollTo]
  );

  const [, { pending }] = useApi(submitFormPayment);

  const isDeposit = Payment.Action.Deposit;

  const handleChangeReceiveBonuses = useCallback(async () => {
    const { error } = await requestUpdateBonusSettings(!isUserIssueBonuses)
      .request;

    if (error) return;

    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__DEPOSIT__CAN_ISSUE_BONUSES__CLICK,
      attributes: {
        additionalData: JSON.stringify({ canIssue: !isUserIssueBonuses }),
      },
    });

    fetchUser(REASON_FOR_UPDATING_USER.DEPOSIT);
  }, [
    requestUpdateBonusSettings,
    isUserIssueBonuses,
    sendEventToAnalytics,
    fetchUser,
  ]);

  useEffect(() => {
    sendEventToAnalytics({
      name: AnalyticsEventName.MODAL__DEPOSIT__FORM__APPEARANCE,
      attributes: {
        additionalData: JSON.stringify({ from: window.location.href }),
      },
    });
  }, [sendEventToAnalytics]);

  const isCryptoMethod = currentPaymentMethod?.type.includes(CRYPTO_METHOD);

  if (user?.currency == null) return null;

  return (
    <ScDepositWrapper>
      <ScDepositTitle>{t('glossary:deposit')}</ScDepositTitle>
      <ScDepositRow
        ref={refDepositForm}
        isPadding={!!isDeposit && !!isUserIssueBonuses}
      >
        <ScDepositNav>
          <h3 className="payment-method">{t('deposits:allPaymentMethod')}</h3>
          <div className="currency-payments">
            <CurrencyUser />
            <PaymentsMethods
              currentMethod={currentPaymentMethod}
              methods={paymentMethods}
              pending={pendingPaymentMethods}
              onSelect={handleSelectPaymentMethods}
            />
          </div>
        </ScDepositNav>
        <ScDepositContent
          ref={refDepositForm}
          isCryptoMethod={!!isCryptoMethod}
        >
          <ScPaymentMethodCompWrapper isDeposit={!!isDeposit}>
            {pendingPaymentMethods ? (
              <Spinner size={64} center />
            ) : currentPaymentMethod == null ? (
              <ScNotFound>
                {capitalize(t('deposits:notSelectedMethod'))}
              </ScNotFound>
            ) : (
              <PaymentMethodComp
                userCurrency={user.currency}
                paymentMethod={currentPaymentMethod}
                paymentAction={Payment.Action.Deposit}
                setCurrentPaymentMethod={setCurrentPaymentMethod}
              />
            )}
          </ScPaymentMethodCompWrapper>
          {!isBonusRestricted && (
            <ScWrapperPromo userBonuses={isUserIssueBonuses}>
              {isUserIssueBonuses && (
                <div className="title">{t('deposits:bonus.title')}</div>
              )}
              <FormGroup>
                <Checkbox
                  key={`receiveBonuses-${isUserIssueBonuses}`}
                  label={t('deposits:inputPromo.dontWantBonuses')}
                  field="receiveBonuses"
                  initialValue={isUserIssueBonuses}
                  onClick={handleChangeReceiveBonuses}
                />
              </FormGroup>
              {isUserIssueBonuses && (
                <>
                  <DepositBonuses paymentMethod={currentPaymentMethod} />
                  <InputPromo pending={pendingPaymentMethods} />
                </>
              )}
            </ScWrapperPromo>
          )}
          {isDeposit && (
            <div className="deposit-wrapper">
              {!isCryptoMethod && (
                <div className="deposit-button">
                  <Button
                    green
                    xLarge
                    form="depositForm"
                    wide
                    type="submit"
                    pending={pending}
                  >
                    {t('glossary:deposit')}
                  </Button>
                </div>
              )}
              <p className="help-wrapper">
                {t('deposits:help.title')}
                <span id="intercomcustomLauncher">
                  {t('deposits:help.description')}
                </span>
              </p>
            </div>
          )}
        </ScDepositContent>
      </ScDepositRow>
    </ScDepositWrapper>
  );
};

const CurrencyUser = React.memo(() => {
  const { t } = useTranslation('glossary');

  const { wallets, changeUserWallet, user } = useContext(AuthContext);
  const { isMobile } = useContext(CommonUtilsContext);

  const { currencies } = useContext(CommonContext);
  const { handleModal } = useContext(ModalsContext);

  const formApiRef = useRef<FormApi<CurrencyApi>>();

  const optionsWallets = useMemo(() => {
    return wallets.map((wallet) => {
      const { currency, amount } = getCurrencyCentsAndCode(
        wallet.amountCents,
        wallet.currency,
        currencies
      );

      return {
        name: isMobile ? currency : `${amount} ${currency}`,
        value: wallet.currency,
      };
    });
  }, [wallets, currencies, isMobile]);

  useEffect(() => {
    if (user?.currency && formApiRef.current) {
      formApiRef.current.setValue('currency', user.currency);
    }
  }, [user?.currency]);

  const handleMobileClick = useCallback(() => {
    return handleModal(ModalType.MyCurrencies, {
      stayInSameWindow: true,
    });
  }, [handleModal]);

  const handleDesktopClick = useCallback(() => {
    return handleModal(ModalType.EditCurrencies, {
      stayInSameWindow: true,
    })();
  }, [handleModal]);

  return (
    <ScCurrencyUser>
      <h3>{t('currency')}</h3>
      <div className="select-wallet">
        <Form<CurrencyApi>
          getApi={(api) => {
            formApiRef.current = api;
          }}
        >
          <Select
            field="currency"
            options={optionsWallets}
            onCustomOpen={isMobile ? handleMobileClick() : undefined}
            animatedPlaceholder={t('glossary:currency')}
            maxHeight={124}
            dropBoxShadow
            onSelect={(value: any) => {
              changeUserWallet(value);
            }}
            withBorderMode
            addons={
              <Button grayDarker small onClick={() => handleDesktopClick()}>
                {t('newCurrency')}
              </Button>
            }
          />
        </Form>
      </div>
    </ScCurrencyUser>
  );
});

interface PaymentsMethodsProps {
  pending: boolean;
  onSelect: (method: Payment.Method) => void;
  currentMethod?: Payment.Method;
  methods?: Payment.Method[];
}

const PaymentsMethods = React.memo(
  ({ currentMethod, methods, pending, onSelect }: PaymentsMethodsProps) => {
    const { t } = useTranslation(['deposits', 'glossary']);
    const { isMobile } = useContext(CommonUtilsContext);
    const formApiRef = useRef<FormApi<MethodApi>>();

    const handleMobileSelect = (value: string) => {
      const selected = options.find((opt) => opt.value === value);
      if (selected?.method) {
        onSelect(selected.method);
      }
    };

    const options = useMemo(() => {
      return methods
        ? methods.map((method, index) => ({
            value: method.id,
            name: method.brand,
            label: (
              <PaymentMethodCardMobile
                key={method.id}
                method={method}
                index={index}
                onSelect={onSelect}
              />
            ),
            method,
          }))
        : [];
    }, [methods, onSelect]);

    const { fiatMethods, cryptoMethods } = useMemo(() => {
      if (!methods) return { fiatMethods: [], cryptoMethods: [] };

      return methods.reduce<{
        fiatMethods: Payment.Method[];
        cryptoMethods: Payment.Method[];
      }>(
        (acc, method) => {
          if (method.type.includes(CRYPTO_METHOD)) {
            acc.cryptoMethods.push(method);
          } else {
            acc.fiatMethods.push(method);
          }
          return acc;
        },
        { fiatMethods: [], cryptoMethods: [] }
      );
    }, [methods]);

    useEffect(() => {
      if (formApiRef.current && currentMethod?.id) {
        formApiRef.current.setValue('method', currentMethod.id);
      }
    }, [currentMethod?.id]);

    return (
      <ScPaymentsMethods>
        {pending ? (
          <Spinner size={64} center />
        ) : methods == null || methods.length === 0 ? (
          <ScNotFound>{t('deposits:notFoundPaymentsMethods')}</ScNotFound>
        ) : (
          <>
            {isMobile ? (
              <Form<MethodApi>
                getApi={(api) => {
                  formApiRef.current = api;
                }}
              >
                <Select
                  field="method"
                  options={options}
                  initialValue={currentMethod?.id}
                  onSelect={(value: any) => handleMobileSelect(value)}
                  forceDesktopView
                  dropBoxShadow
                  maxHeight={172}
                  withBorderMode
                />
              </Form>
            ) : (
              <>
                {fiatMethods && (
                  <>
                    <h3>{t('glossary:payments')}</h3>
                    <div className="payments">
                      {fiatMethods.map((method, index) => (
                        <PaymentMethodCard
                          key={method.id}
                          method={method}
                          index={index}
                          onSelect={onSelect}
                          currentMethod={currentMethod}
                        />
                      ))}
                    </div>
                  </>
                )}
                {cryptoMethods && (
                  <>
                    <h3 className="crypto-title">
                      {t('glossary:cryptoMethods')}
                    </h3>
                    <div className="payments">
                      {cryptoMethods.map((method, index) => (
                        <PaymentMethodCard
                          key={method.id}
                          method={method}
                          index={index}
                          onSelect={onSelect}
                          currentMethod={currentMethod}
                        />
                      ))}
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
      </ScPaymentsMethods>
    );
  }
);
